"use strict";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useLayoutEffect } from "react";
import { Navigate, Route, Routes as RouterRoutes, useLocation } from "react-router";
import { useAlert } from "capitalrx-components-2.0/lib/general";
import { clearModuleMetadata } from "capitalrx-components-2.0/lib/general/HotKeys";
import { AsyncWrapper } from "./AsyncWrapper";
import { ContentLayout } from "./ContentLayout";
import { Error404Page } from "./Error404Page";
import { activateModuleMetadata } from "./api";
import { BodyModuleClass, ModuleErrorBoundary } from "./components";
import { HostModuleContext, OwnerModuleContext, ShellContext } from "./context";
import { useIsFullscreenRoute, useModulePathResolver, useModules, useRootModule, useRouteCache, useShellModulePathResolver } from "./hooks";
const ModuleMetadataActivator = ({ moduleId, children }) => {
  useEffect(() => {
    activateModuleMetadata(moduleId);
    return () => {
      clearModuleMetadata();
    };
  }, [moduleId]);
  return /* @__PURE__ */ jsx(Fragment, { children });
};
const ContentRoutes = () => {
  var _a;
  const modules = useModules();
  const resolvePath = useModulePathResolver();
  const path404 = resolvePath("/404");
  let path = (_a = useLocation()) == null ? void 0 : _a.pathname;
  if (path.endsWith("/")) path = path.substr(0, path.length - 1);
  return /* @__PURE__ */ jsxs(RouterRoutes, { children: [
    modules.map((module) => {
      const { info } = module;
      const { id } = info;
      if (!module.index) return null;
      const containerProvider = async () => {
        var _a2;
        switch (module.version) {
          case 1:
          case void 0:
            return module.container;
          case 2:
            return await ((_a2 = module.container) == null ? void 0 : _a2.call(module));
          default:
            return void 0;
        }
      };
      const indexProvider = async () => {
        var _a2;
        switch (module.version) {
          case 1:
          case void 0:
            return module.index;
          case 2:
            return await ((_a2 = module.index) == null ? void 0 : _a2.call(module));
          default:
            return void 0;
        }
      };
      return /* @__PURE__ */ jsx(
        Route,
        {
          path: `${id}/*`,
          element: /* @__PURE__ */ jsx(ModuleErrorBoundary, { children: /* @__PURE__ */ jsx(ModuleMetadataActivator, { moduleId: id, children: /* @__PURE__ */ jsx(OwnerModuleContext.Provider, { value: info, children: /* @__PURE__ */ jsxs(HostModuleContext.Provider, { value: info, children: [
            /* @__PURE__ */ jsx(BodyModuleClass, {}),
            /* @__PURE__ */ jsx(AsyncWrapper, { resolver: containerProvider, children: /* @__PURE__ */ jsx(AsyncWrapper, { resolver: indexProvider }) })
          ] }) }) }) })
        },
        id
      );
    }),
    /* @__PURE__ */ jsx(Route, { path: "*", element: /* @__PURE__ */ jsx(Navigate, { to: path404, replace: true }) })
  ] });
};
export const Routes = (props) => {
  var _a;
  const { children } = props;
  const location = useLocation();
  const showAlert = useAlert();
  const { moduleRootPath } = useContext(ShellContext);
  const resolvePath = useShellModulePathResolver();
  const rootModule = useRootModule();
  const modules = useModules();
  const routeCache = useRouteCache(modules);
  useLayoutEffect(() => {
    globalThis.scrollTo(0, 0);
  }, [location.pathname]);
  useEffect(() => {
    var _a2;
    const alertInfo = (_a2 = location == null ? void 0 : location.state) == null ? void 0 : _a2.alert;
    if (alertInfo == null ? void 0 : alertInfo.message) {
      showAlert(alertInfo);
    }
  }, [(_a = location == null ? void 0 : location.state) == null ? void 0 : _a.alert, showAlert]);
  const isFullscreen = useIsFullscreenRoute(location.pathname, routeCache, moduleRootPath);
  return /* @__PURE__ */ jsx(ContentLayout, { fullscreen: isFullscreen, children: /* @__PURE__ */ jsxs(RouterRoutes, { children: [
    rootModule && /* @__PURE__ */ jsx(Route, { path: "/", element: /* @__PURE__ */ jsx(Navigate, { to: resolvePath(rootModule, "/") }) }),
    /* @__PURE__ */ jsx(Route, { path: `${moduleRootPath}/*`, element: /* @__PURE__ */ jsx(ContentRoutes, {}) }),
    /* @__PURE__ */ jsx(Route, { path: "/404", element: /* @__PURE__ */ jsx(Error404Page, {}) }),
    /* @__PURE__ */ jsx(Route, { path: "/*", element: children ? children() : /* @__PURE__ */ jsx(Error404Page, {}) })
  ] }) });
};
